import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IOnSubmitArgs, OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { updateVisitorWorkflow, updateVisitorWorkflowConfig } from 'store/slices/visitorWorkflowsSlice';
import { List } from 'antd';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { OpRow } from 'components/customAntd/DLS/OpRow/OpRow';
import { OpCol } from 'components/customAntd/DLS/OpCol/OpCol';
import locationIcon from '../../location.svg';
import { hasPermission } from 'utils/utils';
import { useTranslation } from 'react-i18next';

interface WorkflowContentProps {
    form: any;
}

const WorkflowContent: React.FC<WorkflowContentProps> = ({ form }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { selectedVisitorWorkflow, selectedVisitorWorkflowLocations: visitorWorkflowLocations, selectedSignInWorkflowConfig: visitorWorkflowConfig } = useSelector((state: RootState) => state.visitorWorkflows);
    const [isScanScannerDisabled, setScanScannerDisabled] = useState(true);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode); // Accessing isDarkMode from Redux

    const hasSigninconfRead = hasPermission(tokenScopeList, orgId, 'o', 'signinconf:r');
    const hasSigninconfWrite = hasPermission(tokenScopeList, orgId, 'o', 'signinconf:w');

    const { t } = useTranslation();

    const idScanData = [
        { name: 'scan', label: t('workflow.attendantSignIn.form.scanToIdentify'), valuePropName: 'checked' },
        { name: 'scanScanner', label: t('workflow.attendantSignIn.form.scanScanner'), valuePropName: 'checked', dependent: true },
    ];

    const notificationData = [
        { name: 'notifyHost', label: t('workflow.attendantSignIn.form.notifyHost'), valuePropName: 'checked' },
    ];

    useEffect(() => {
        const initialScanIdState = visitorWorkflowConfig?.scan;
        setScanScannerDisabled(!initialScanIdState);
        if (!initialScanIdState) {
            form.setFieldsValue({ scanScanner: false });
        }
    }, [visitorWorkflowConfig, form]);

    const initialValues = useMemo(() => {
        if (selectedVisitorWorkflow) {
            return {
                name: selectedVisitorWorkflow.name,
                workflowType: t('workflow.attendantSignIn.workflowType'),
                scan: visitorWorkflowConfig?.scan,
                scanScanner: visitorWorkflowConfig?.scanScanner,
                notifyHost: visitorWorkflowConfig?.notifyHost,
            };
        }
        return {};
    }, [t, selectedVisitorWorkflow, visitorWorkflowConfig?.notifyHost, visitorWorkflowConfig?.scan, visitorWorkflowConfig?.scanScanner]);

    const handleSubmit = useCallback(async ({ values, touchedValues, initialValues }: IOnSubmitArgs<any>) => {
        const visitorWorkflowConfigParams = {
            scan: values.scan ? 1 : 0,
            scanScanner: values.scanScanner ? 1 : 0,
            notifyHost: values.notifyHost ? 1 : 0
        };
        if (selectedVisitorWorkflow) {
            await dispatch(updateVisitorWorkflow({ orgId, visitorWorkflowId: selectedVisitorWorkflow.id, visitorWorkflow: { name: touchedValues.name } })).unwrap();
            await dispatch(updateVisitorWorkflowConfig({ orgId, visitorWorkflowId: selectedVisitorWorkflow.id, visitorWorkflowConfigParams })).unwrap();
        }
    }, [dispatch, orgId, selectedVisitorWorkflow]);


    const handleScanChange = (checked: boolean) => {
        setScanScannerDisabled(!checked);
        if (!checked) {
            form.setFieldsValue({ scanScanner: false });
        }
    };

    const locationIconStyle = {
        marginRight: '8px',
        width: '16px',
        height: '16px',
        filter: isDarkMode ? 'invert(1)' : 'none',
    };

    return (
        <OpForm
            form={form}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            hasError={false}
            defaultButtons={false}
            isReadOnly={!hasSigninconfWrite && hasSigninconfRead}
        >
            <OpSpace
                direction="vertical"
                size="middle"
                style={{
                    display: 'flex',
                }}
            >
                <OpRow gutter={16}>
                    <OpCol span={12}>
                        <OpForm.Input label={t('workflow.attendantSignIn.form.name')} name="name" rules={[{ required: true, message: t('workflow.attendantSignIn.form.nameRequired') }]} />
                    </OpCol>
                    <OpCol span={12}>
                        <OpForm.Input disabled label={t('workflow.attendantSignIn.form.workflowType')} name="workflowType" />
                    </OpCol>
                </OpRow>

                <List
                    header={t('workflow.attendantSignIn.form.scanTitle')}
                    dataSource={idScanData}
                    bordered
                    renderItem={item => (
                        <List.Item>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <OpForm.Switch name={item.name} style={{ marginBottom: 0 }}
                                    disabled={item.dependent && (isScanScannerDisabled || (!hasSigninconfWrite && hasSigninconfRead))}
                                    onChange={item.name === 'scan' ? handleScanChange : undefined} />
                                <span style={{ marginLeft: 8 }}>{item.label}</span>
                            </div>
                        </List.Item>
                    )} />

                <List
                    header={t('workflow.attendantSignIn.form.notificationTitle')}
                    dataSource={notificationData}
                    bordered
                    renderItem={item => (
                        <List.Item>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <OpForm.Switch
                                    name={item.name} style={{ marginBottom: 0 }}
                                    disabled={!hasSigninconfWrite && hasSigninconfRead}
                                />
                                <span style={{ marginLeft: 8 }}>{item.label}</span>
                            </div>
                        </List.Item>
                    )} />

                <List
                    header={t('workflow.attendantSignIn.form.locationsTitle')}
                    bordered
                    dataSource={visitorWorkflowLocations.data.filter(location => location.status === 1)}
                    renderItem={location => (
                        <List.Item>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%', color: isDarkMode ? '#FFFFFF' : '#000000' }}>
                                <img src={locationIcon} alt="location icon" style={locationIconStyle} />
                                <span>{location.name}</span>
                            </div>
                        </List.Item>
                    )}
                />
            </OpSpace>
        </OpForm>
    );
};

export default WorkflowContent;
